// variables-table.component.ts
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ObservationVariable } from '../../../shared/model/observation-variable.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StudyVariableSelectModalComponent } from '../modals/variable-select-modal/study-variable-select-modal.component';
import { VariableTypeEnum } from '../../../shared/ontology/variable-type.enum';
import { Subscription } from 'rxjs';
import { VariableDetails } from '../../../shared/ontology/model/variable-details';
import { AlertService } from '../../../shared/alert/alert.service';
import { ObservationVariableItem } from '../models/observation-variable-item.model';
import { preventDefaultOnShiftKey } from '../../../field-management/utils';

@Component({
    selector: 'jhi-variables-table',
    templateUrl: './variables-table.component.html',
    styleUrls: ['./variables-table.component.scss']
})
export class VariablesTableComponent implements OnInit, OnChanges, OnDestroy {

    subscription: Subscription;

    protected readonly preventDefaultOnShiftKey = preventDefaultOnShiftKey;

    @Input() variables: ObservationVariableItem[] = [];
    @Input() showReorderButtons = true;
    @Input() showDeleteButton = true;
    @Input() showAddButton = true;
    @Input() showEditAliasButton = false;
    @Input() showInputVariablesColumn = true;
    @Input() variableTypeIds: VariableTypeEnum[];
    @Input() hiddenVariablesFromTable: number[] = [];
    @Input() hiddenVariablesFromSelector: number[] = [];
    @Input() preventAddSystemVariables!: boolean;
    @Input() disableVariablesFromSelector!: number[];

    @Output() onAddEvent = new EventEmitter<VariableDetails>();
    @Output() onRemoveEvent = new EventEmitter<ObservationVariable[]>();
    @Output() onMoveEvent = new EventEmitter<{selectedVariable: ObservationVariable, variableIdForSwitching: number, movement: 'up' | 'down'}>();
    @Output() onVariableSelectModalClosed = new EventEmitter<void>();
    @Output() onRenameEvent = new EventEmitter<ObservationVariable>();
    @Input() isVariableMoving: boolean = false;

    hideVariable = false;
    isSelectAll = false;
    filteredVariables: ObservationVariableItem[] = [];
    movingVariableTermId: number;
    lastClickIndex: any;
    selectedItems: Set<number> = new Set<number>();


    constructor(public modalService: NgbModal,
                private alertService: AlertService) {

    }

    ngOnInit(): void {
        this.updateFilteredVariables();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.variables && !changes.variables.firstChange) {
            this.updateFilteredVariables();
        }
    }

    updateFilteredVariables(): void {
        this.selectedItems.clear();
        this.filteredVariables = this.variables.filter(variable => {
            variable.deletable = !variable.systemVariable;
            if (variable.termId === this.movingVariableTermId) {
                this.selectedItems.add(variable.termId);
            }
            // Only show variables that are not in the hidden list
            return !this.hiddenVariablesFromTable.includes(variable.termId);
        });
        this.updateSelectAllState();
    }

    getSelectableItems(): ObservationVariableItem[] {
        return this.filteredVariables.filter(v => v.deletable && !v.hidden);
    }

    getSelectedItems(): ObservationVariableItem[] {
        return this.filteredVariables.filter(v => v.deletable && !v.hidden && this.selectedItems.has(v.termId));
    }

    findIndex(observationVariable: ObservationVariableItem): number {
        if (observationVariable) {
            return this.filteredVariables.findIndex(v => v.termId === observationVariable.termId);
        }
    }

    onRemoveVariables() {

        const selectedVariables = this.variables.filter(v => this.selectedItems.has(v.termId));
        // Implement remove logic here
        if (this.onRemoveEvent) {
            this.onRemoveEvent.emit(selectedVariables);

        }
    }

    onAddVariableClick(): void {
        const modal = this.modalService.open(StudyVariableSelectModalComponent as Component, {
            modalDialogClass: 'modal-lg',
            backdrop: 'static'
        });
        const modalRef = modal.componentInstance;
        modalRef.variableTypeIds = this.variableTypeIds;
        modalRef.excludedVariableIds = this.hiddenVariablesFromSelector;
        modalRef.existingVariableIdsInStudy = this.variables.map(v => v.termId);
        modalRef.disableVariablesFromSelector = this.disableVariablesFromSelector;
        modalRef.preventAddSystemVariables = this.preventAddSystemVariables;

        this.subscription = modalRef.variableSelect.subscribe((variable) => {
            if (variable) {
                this.onAddEvent.emit(variable);
            }
        });

        // Clean up when modal is closed
        modal.result.then(() => {
            if (this.subscription) {
                this.subscription.unsubscribe();
            }
            this.onVariableSelectModalClosed.emit();
        });
    }

    reorderTrait(movement: 'up' | 'down'): void {
        if (this.onMoveEvent) {
            const selectedVariables = this.variables.filter(v => this.selectedItems.has(v.termId));
            const selectedVariable = selectedVariables[0];
            this.movingVariableTermId = selectedVariable.termId;
            const index = this.findIndex(selectedVariable);

            if (index !== -1) {
                this.onMoveEvent.emit({
                    selectedVariable: selectedVariable,
                    variableIdForSwitching: this.filteredVariables[movement === 'up' ? index - 1 : index + 1].termId,
                    movement
                });
                if (movement === 'up' && index > 0) {
                    const temp = this.filteredVariables[index - 1];
                    this.filteredVariables[index - 1] = this.filteredVariables[index];
                    this.filteredVariables[index] = temp;
                } else if (movement === 'down' && index < this.filteredVariables.length - 1) {
                    const temp = this.filteredVariables[index + 1];
                    this.filteredVariables[index + 1] = this.filteredVariables[index];
                    this.filteredVariables[index] = temp;
                }
            }
        }
    }

    doSelectAll(): void {
        if (!this.isSelectAll) {
            this.selectedItems.clear();
        } else {
            this.getSelectableItems().forEach(variable => {
                if (variable.deletable) {
                    this.selectedItems.add(variable.termId);
                }
            });
        }
    }

    updateSelectAllState(): void {
        const removableVariables = this.getSelectableItems();
        this.isSelectAll = removableVariables.length > 0 &&
            removableVariables.every(variable => this.selectedItems.has(variable.termId));
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this.selectedItems.clear();
    }

    toggleSelect($event, index: number, item: ObservationVariableItem, checkbox = false) {


        if ($event.ctrlKey || $event.metaKey) {
            $event.preventDefault();
        }

        if (!checkbox) {
            $event.preventDefault();
            $event.stopPropagation();
        }

        if (!$event.ctrlKey && !$event.metaKey && !checkbox) {
            this.selectedItems.clear();
        }

        let items = [];
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1;
            const min = Math.min(this.lastClickIndex, index);
            items = this.filteredVariables.slice(min, max);
        } else {
            items = [item];
            this.lastClickIndex = index;
        }

        const isClickedItemSelected = this.selectedItems.has(item.termId);
        for (const item of items) {
            if (this.isSelectable(item)) {
                if (isClickedItemSelected) {
                    item.isChecked = false;
                    this.selectedItems.delete(item.termId);
                } else {
                    item.isChecked = true;
                    this.selectedItems.add(item.termId);
                }
            }
        }
    }

    isSelectable(item: ObservationVariableItem) {
        return item.deletable && this.showDeleteButton;
    }

    isSelected(termId: number): boolean {
        return this.selectedItems.has(termId);
    }

    onEditAlias() {

        const selectedVariables = this.variables.filter(v => this.selectedItems.has(v.termId));
        const selectedVariable = selectedVariables[0];

        if (this.onRenameEvent) {
            this.onRenameEvent.emit(selectedVariable);
        }
    }
}


